import React from "react";
import Alert from "./Alert";
import {
  XCircleIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  InfoIcon,
} from "../icons";

// Error types for enhanced error handling
const ErrorTypes = {
  NETWORK: "NETWORK",
  AUTH: "AUTH",
  VALIDATION: "VALIDATION",
  SERVER: "SERVER",
  NOT_FOUND: "NOT_FOUND",
  TIMEOUT: "TIMEOUT",
  RATE_LIMIT: "RATE_LIMIT",
  UNKNOWN: "UNKNOWN",
};

/**
 * Unified ErrorDisplay component - consolidates ErrorDisplay and EnhancedErrorDisplay
 *
 * @param {Object} props
 * @param {string|Object} props.error - Error message string or AppError object
 * @param {Function} props.onDismiss - Function to call when dismissed
 * @param {boolean} props.showDetails - Whether to show technical details
 * @param {React.ReactNode} props.action - Optional action element
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.variant - Force specific variant (overrides auto-detection)
 * @param {string} props.title - Custom title (overrides auto-generated)
 * @param {boolean} props.enhanced - Use enhanced mode with icons and structured layout
 */
const UnifiedErrorDisplay = ({
  error,
  onDismiss,
  showDetails = false,
  action = null,
  className = "",
  variant = null,
  title = null,
  enhanced = true,
}) => {
  // If no error, render nothing
  if (!error) return null;

  // Normalize error to object format
  const errorObj =
    typeof error === "string"
      ? { message: error, errorType: ErrorTypes.UNKNOWN }
      : error;

  // Auto-detect error type from message if not provided
  const detectErrorType = (message) => {
    const msg = message?.toLowerCase() || "";
    if (msg.includes("network") || msg.includes("connection"))
      return ErrorTypes.NETWORK;
    if (
      msg.includes("auth") ||
      msg.includes("unauthorized") ||
      msg.includes("forbidden")
    )
      return ErrorTypes.AUTH;
    if (
      msg.includes("validation") ||
      msg.includes("invalid") ||
      msg.includes("required")
    )
      return ErrorTypes.VALIDATION;
    if (msg.includes("server") || msg.includes("internal"))
      return ErrorTypes.SERVER;
    if (msg.includes("not found") || msg.includes("404"))
      return ErrorTypes.NOT_FOUND;
    if (msg.includes("timeout") || msg.includes("slow"))
      return ErrorTypes.TIMEOUT;
    if (msg.includes("rate") || msg.includes("limit"))
      return ErrorTypes.RATE_LIMIT;
    return ErrorTypes.UNKNOWN;
  };

  const errorType = errorObj.errorType || detectErrorType(errorObj.message);

  // Map error types to alert variants
  const variantMap = {
    [ErrorTypes.NETWORK]: "warning",
    [ErrorTypes.AUTH]: "danger",
    [ErrorTypes.VALIDATION]: "warning",
    [ErrorTypes.SERVER]: "danger",
    [ErrorTypes.NOT_FOUND]: "warning",
    [ErrorTypes.TIMEOUT]: "warning",
    [ErrorTypes.RATE_LIMIT]: "info",
    [ErrorTypes.UNKNOWN]: "danger",
  };

  // Map error types to icons
  const IconMap = {
    [ErrorTypes.NETWORK]: AlertTriangleIcon,
    [ErrorTypes.AUTH]: XCircleIcon,
    [ErrorTypes.VALIDATION]: AlertCircleIcon,
    [ErrorTypes.SERVER]: XCircleIcon,
    [ErrorTypes.NOT_FOUND]: InfoIcon,
    [ErrorTypes.TIMEOUT]: AlertTriangleIcon,
    [ErrorTypes.RATE_LIMIT]: InfoIcon,
    [ErrorTypes.UNKNOWN]: XCircleIcon,
  };

  // Generate titles based on error type
  const titleMap = {
    [ErrorTypes.NETWORK]: "Connection Error",
    [ErrorTypes.AUTH]: "Authentication Error",
    [ErrorTypes.VALIDATION]: "Validation Error",
    [ErrorTypes.SERVER]: "Server Error",
    [ErrorTypes.NOT_FOUND]: "Not Found",
    [ErrorTypes.TIMEOUT]: "Request Timeout",
    [ErrorTypes.RATE_LIMIT]: "Rate Limited",
    [ErrorTypes.UNKNOWN]: "Error",
  };

  const alertVariant = variant || variantMap[errorType] || "danger";
  const errorTitle = title || titleMap[errorType] || "Error";
  const ErrorIcon = IconMap[errorType] || XCircleIcon;

  // Simple mode (legacy ErrorDisplay behavior)
  if (!enhanced) {
    return (
      <Alert
        variant={alertVariant}
        dismissible={!!onDismiss}
        onDismiss={onDismiss}
        className={`my-4 ${className}`}
      >
        <p>{errorObj.message}</p>
      </Alert>
    );
  }

  // Enhanced mode with icons and structured layout
  return (
    <Alert
      variant={alertVariant}
      dismissible={!!onDismiss}
      onDismiss={onDismiss}
      className={`flex items-start p-4 ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start w-full">
        <div className="flex-shrink-0 mr-3">
          <ErrorIcon className="w-5 h-5" />
        </div>

        <div className="flex-grow min-w-0">
          <h4 className="font-semibold text-sm">{errorTitle}</h4>
          <p className="text-sm mt-1 break-words">{errorObj.message}</p>

          {/* Show technical details if enabled */}
          {showDetails && errorObj.details && (
            <div className="mt-2 text-xs bg-black/5 dark:bg-white/5 p-2 rounded">
              <pre className="whitespace-pre-wrap text-xs overflow-x-auto">
                {typeof errorObj.details === "string"
                  ? errorObj.details
                  : JSON.stringify(errorObj.details, null, 2)}
              </pre>
            </div>
          )}

          {/* Show error code if available */}
          {showDetails && errorObj.errorCode && (
            <p className="text-xs mt-1 opacity-75">
              Error code: {errorObj.errorCode}
            </p>
          )}

          {/* Show timestamp if available */}
          {showDetails && errorObj.timestamp && (
            <p className="text-xs mt-1 opacity-75">
              Time: {new Date(errorObj.timestamp).toLocaleString()}
            </p>
          )}

          {/* Action buttons or links */}
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </Alert>
  );
};

UnifiedErrorDisplay.displayName = "UnifiedErrorDisplay";

// Export error types for use by other components
export { ErrorTypes };

export default UnifiedErrorDisplay;

// Backward compatibility exports
export const ErrorDisplay = (props) => (
  <UnifiedErrorDisplay enhanced={false} {...props} />
);
export const EnhancedErrorDisplay = (props) => (
  <UnifiedErrorDisplay enhanced={true} {...props} />
);
